import cn from 'classnames'
import Link from 'next/link'
import { useEffect, useId, useRef, useState } from 'react'
import type { MouseEvent, ReactElement } from 'react'
import { useTranslation } from 'next-i18next/pages'

import type { AssociatedProductsContext } from '@lib/hooks/product/associated-products/getAssociatedProductsConfig'
import { useAssociatedProductsLimit } from '@lib/hooks/product/associated-products/useAssociatedProductsConfig'
import s from './AssociatedProductThumbs.module.scss'

/**
 * EVO-263: en Casaideas cada color es un configurable aparte, asi que los
 * colores hermanos llegan en `associated_products` (link type nuevo de Magento
 * expuesto por el edge) y NO en `variants`.
 *
 * Cada miniatura con stock es un `next/link` a la PDP del hermano: la
 * navegacion es client-side (la PDP es ISR + SWR), por lo que foto principal,
 * precio y stock de tallas se actualizan sin recargar la pagina y la URL queda
 * en el SKU clickeado.
 *
 * Los hermanos `OUT_OF_STOCK` ahora llegan tambien (Magento dejo de filtrarlos)
 * y se pintan con una capa "SIN STOCK" encima, SIN enlace y sin foco, igual que
 * las tallas agotadas de EVO-245.
 *
 * El tope de miniaturas visibles sale del storeConfig segun el contexto
 * (`plp` / `srp` / `pdp`); el excedente se resume en un boton "+N" que despliega
 * el resto en el sitio (ya no navega a la PDP).
 */

const PRODUCT_URL_PREFIX = '/producto/'
const PRODUCT_URL_SUFFIX = '.html'
const OUT_OF_STOCK = 'OUT_OF_STOCK'
/** Ancho pedido al CDN de imagenes; la miniatura mas grande es 56px (2x retina). */
const DEFAULT_IMAGE_WIDTH = 120

export interface AssociatedProductImage {
    url?: string | null
    label?: string | null
}

export interface AssociatedProduct {
    __typename?: string | null
    id?: number | string | null
    uid?: string | null
    name?: string | null
    sku?: string | null
    url_key?: string | null
    stock_status?: string | null
    /** URL resuelta del rol `swatch_image` (foto de fondo blanco). */
    swatch_image_data?: AssociatedProductImage | null
    small_image?: AssociatedProductImage | null
    thumbnail?: AssociatedProductImage | null
}

export interface AssociatedProductThumbsProps {
    /** `associated_products` tal como llega del edge. */
    products?: ReadonlyArray<AssociatedProduct | null | undefined> | null
    /** SKU del producto que se esta viendo, para marcarlo dentro de la lista. */
    currentSku?: string | null
    /**
     * Titulo ya resuelto (PDP). En PLP/SRP no se pinta titulo, asi que los
     * tiles simplemente no lo pasan. La cascada
     * producto -> config -> i18n vive en
     * `useAssociatedProductsTitle` / `resolveAssociatedProductsTitle`.
     */
    title?: string
    /**
     * De donde sale el tope de miniaturas visibles y con que tamano se pintan:
     * `plp` y `srp` usan la miniatura chica, `pdp` la grande.
     */
    context?: AssociatedProductsContext
    /**
     * Tope explicito. Si no se pasa, sale del storeConfig segun `context`
     * (`associated_products_{plp,srp,pdp}_limit`, con fallback a 4).
     */
    limit?: number
    /** `width` del query param de imagen, igual que los tiles. */
    imageWidth?: number
    /**
     * PLP/SRP: intercambia el producto del tile en vez de navegar a su PDP.
     *
     * Cuando se pasa, el click primario "simple" se cancela y se delega aqui; el
     * resto de las formas de abrir un enlace (ctrl/cmd, rueda, menu contextual,
     * "abrir en pestana nueva") siguen llevando a la PDP del hermano, y el `href`
     * real se mantiene para el rastreo y el teclado. La PDP no pasa este callback,
     * asi que ahi el comportamiento no cambia: navega.
     */
    onSelect?: (product: AssociatedProduct) => void
    className?: string
}

/**
 * Mismo tratamiento de query params que los tiles
 * (`ProductTilePlp` / `ProductTileMobile`).
 */
export const associatedThumbImageUrl = (
    imageUrl?: string | null,
    width: number = DEFAULT_IMAGE_WIDTH
): string => {
    if (!imageUrl) return ''
    const widthString = width ? `&width=${width}` : ''
    return `${imageUrl}?auto=webp&format=pjpg${widthString}`
}

/**
 * Regla de "fondo blanco" acordada: primero el rol `swatch_image`, luego la
 * imagen de catalogo. `thumbnail` queda como ultimo recurso porque el contrato
 * ya lo pide y evita miniaturas vacias.
 */
export const associatedThumbSource = (
    product: AssociatedProduct
): string | null =>
    product.swatch_image_data?.url ??
    product.small_image?.url ??
    product.thumbnail?.url ??
    null

/**
 * Un click "simple" es el unico que se intercepta. Ctrl/cmd, shift, alt, la rueda
 * o el boton secundario tienen que seguir abriendo la PDP del hermano, que es lo
 * que el usuario pide al usarlos; cancelarlos romperia "abrir en pestana nueva".
 */
export const isPlainLeftClick = (event: MouseEvent): boolean =>
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey

const normalizeSku = (sku?: string | null): string =>
    typeof sku === 'string' ? sku.trim().toUpperCase() : ''

const buildProductHref = (urlKey: string): string =>
    `${PRODUCT_URL_PREFIX}${urlKey}${PRODUCT_URL_SUFFIX}`

/**
 * La fila nunca debe mostrar dos veces el mismo color.
 *
 * Hace falta por dos caminos: la PLP y la PDP antepone el producto que se esta viendo a su propia
 * lista de hermanos —para poder marcarlo como seleccionado—, y los datos pueden traer una
 * autorreferencia `A -> A`, que Magento descarta al cerrar el grupo pero que puede haber quedado
 * de una carga a mano o por REST.
 *
 * Gana la primera aparicion, que es la del producto actual.
 */
const dedupeBySku = (products: AssociatedProduct[]): AssociatedProduct[] => {
    const seen = new Set<string>()

    return products.filter((product) => {
        const key = normalizeSku(product.sku) || (product.url_key ?? '')

        if (seen.has(key)) {
            return false
        }

        seen.add(key)

        return true
    })
}

const isRenderable = (
    product?: AssociatedProduct | null
): product is AssociatedProduct =>
    Boolean(product && typeof product.url_key === 'string' && product.url_key)

export const AssociatedProductThumbs = ({
    products,
    currentSku,
    title,
    context = 'plp',
    limit,
    imageWidth = DEFAULT_IMAGE_WIDTH,
    onSelect,
    className
}: AssociatedProductThumbsProps): ReactElement | null => {
    const { t } = useTranslation('common')
    const configLimit = useAssociatedProductsLimit(context)
    const [isExpanded, setIsExpanded] = useState(false)
    const listRef = useRef<HTMLUListElement>(null)
    const listId = useId()

    const items = dedupeBySku((products ?? []).filter(isRenderable))

    // Al desplegar, el foco pasa a la primera miniatura navegable recien
    // revelada: el boton "+N" desaparece y el teclado no queda huerfano.
    useEffect(() => {
        if (!isExpanded) return

        listRef.current
            ?.querySelector<HTMLElement>('a[data-revealed="true"]')
            ?.focus()
    }, [isExpanded])

    if (!items.length) return null

    const effectiveLimit = typeof limit === 'number' ? limit : configLimit
    const collapsedCount =
        effectiveLimit > 0
            ? Math.min(Math.floor(effectiveLimit), items.length)
            : items.length
    const visibleItems = isExpanded ? items : items.slice(0, collapsedCount)
    const hiddenCount = items.length - visibleItems.length
    const normalizedCurrentSku = normalizeSku(currentSku)

    return (
        <div
            className={cn(
                s['associated-products'],
                { [s['size-pdp']]: context === 'pdp' },
                className
            )}
            data-testid="associated-product-thumbs"
            data-context={context}
        >
            {title ? <p className={s['title']}>{title}</p> : null}
            <ul className={s['list']} id={listId} ref={listRef}>
                {visibleItems.map((product, index) => {
                    const href = buildProductHref(product.url_key as string)
                    const label =
                        product.swatch_image_data?.label ??
                        product.name ??
                        product.sku ??
                        ''
                    const imageSource = associatedThumbSource(product)
                    const isCurrent =
                        Boolean(normalizedCurrentSku) &&
                        normalizeSku(product.sku) === normalizedCurrentSku
                    const isOutOfStock = product.stock_status === OUT_OF_STOCK
                    const isRevealed = isExpanded && index >= collapsedCount

                    const boxClassName = cn(s['box'], s['thumb-box'], {
                        [s['is-current']]: isCurrent,
                        [s['out-of-stock']]: isOutOfStock,
                        [s['interactive']]: !isOutOfStock
                    })

                    const visual = imageSource ? (
                        <img
                            className={s['thumb']}
                            src={associatedThumbImageUrl(
                                imageSource,
                                imageWidth
                            )}
                            alt={label}
                            loading="lazy"
                        />
                    ) : (
                        <span className={s['thumb-text']}>{label}</span>
                    )

                    // Capa "SIN STOCK": texto traducible, visible en PDP y solo
                    // para lectores de pantalla en la miniatura chica de PLP.
                    const outOfStockLayer = isOutOfStock ? (
                        <span className={s['oos-layer']}>
                            <span className={s['oos-label']}>
                                {t('Out of Stock')}
                            </span>
                        </span>
                    ) : null

                    return (
                        <li
                            key={product.sku ?? product.uid ?? href}
                            className={s['item']}
                        >
                            {isOutOfStock ? (
                                // Agotado: NO clickeable y fuera del orden de
                                // tabulacion (coherente con EVO-245).
                                <span
                                    className={boxClassName}
                                    title={label}
                                    data-sku={product.sku ?? ''}
                                    data-out-of-stock="true"
                                    data-revealed={
                                        isRevealed ? 'true' : undefined
                                    }
                                >
                                    {visual}
                                    {outOfStockLayer}
                                </span>
                            ) : (
                                <Link
                                    href={href}
                                    prefetch={!onSelect}
                                    className={boxClassName}
                                    title={label}
                                    aria-current={
                                        isCurrent ? 'true' : undefined
                                    }
                                    data-sku={product.sku ?? ''}
                                    data-revealed={
                                        isRevealed ? 'true' : undefined
                                    }
                                    onClick={
                                        onSelect
                                            ? (event: MouseEvent) => {
                                                  if (
                                                      !isPlainLeftClick(event)
                                                  ) {
                                                      return
                                                  }
                                                  event.preventDefault()
                                                  onSelect(product)
                                              }
                                            : undefined
                                    }
                                >
                                    {visual}
                                </Link>
                            )}
                        </li>
                    )
                })}

                {hiddenCount > 0 ? (
                    <li className={s['item']}>
                        <button
                            type="button"
                            className={cn(s['box'], s['more-button'])}
                            onClick={() => setIsExpanded(true)}
                            aria-expanded={isExpanded}
                            aria-controls={listId}
                            aria-label={t('See {{count}} more colors', {
                                count: hiddenCount
                            })}
                            data-testid="associated-products-more"
                        >
                            {`+${hiddenCount}`}
                        </button>
                    </li>
                ) : null}
            </ul>
        </div>
    )
}

export default AssociatedProductThumbs
